import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { CellSelection } from '@atlaskit/editor-tables/cell-selection';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type {
	ReplaceNodeCommand,
	StreamingCommandFactory,
} from '../aiStreamingOrchestratorPluginType';
import { findNodeByLocalId } from './utils/find-node-by-local-id';

export const createReplaceNodeCommand: StreamingCommandFactory<ReplaceNodeCommand> =
	(api) => (command) => {
		return api.core.actions.execute(({ tr }) => {
			return findNodeByLocalId(api, tr, command.localId, ({ pos, node }) => {
				// Record the exact located node so `buildMountTransaction` can rebuild
				// an accurate single-node `originalUserContentSlice`. Slicing the doc
				// range can return a different shape (e.g. listItem children of a
				// bulletList). The optional call on `recordOriginalNode` itself makes
				// this a no-op when the new AIFC experience gate is off or the action
				// is not present on the resolved AI experience API.
				api.aiExperience?.actions.recordOriginalNode?.(tr, node);

				let selection = null;
				// Lists are not user-selectable, but replaceNode must select the whole
				// target list. A TextSelection is constrained to text inside its items,
				// so replacing a list with a paragraph otherwise leaves the list wrapper.
				const { bulletList, orderedList, taskList, decisionList } = tr.doc.type.schema.nodes;
				if (
					NodeSelection.isSelectable(node) ||
					([bulletList, orderedList, taskList, decisionList].includes(node.type) &&
						fg('platform_editor_ai_replace_list_node_selection'))
				) {
					selection = NodeSelection.create(tr.doc, pos);
				} else {
					const role = node.type.spec.tableRole;
					if (role === 'cell' || role === 'header_cell') {
						selection = CellSelection.create(tr.doc, pos);
					} else if (role === 'row') {
						// There's always a cell after the row
						const $cell = tr.doc.resolve(pos + 1);
						selection = CellSelection.rowSelection($cell, $cell, true);
					} else {
						const $pos = tr.doc.resolve(pos);
						const $end = tr.doc.resolve(pos + node.nodeSize);
						selection = TextSelection.between($pos, $end);
					}
				}
				if (selection) {
					tr.setSelection(selection).scrollIntoView();
				}
			});
		});
	};
